import { useState, useEffect, useCallback } from 'react';

// Mason County, WA — default fallback
const DEFAULT_LAT = 47.35;
const DEFAULT_LNG = -123.0;
const DEFAULT_NAME = 'Mason County, WA';
const STORAGE_KEY = 'hc_weather_location';

function loadSavedLocation() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const loc = JSON.parse(saved);
      // null-check, not falsy — lat/lng of 0 are valid coordinates.
      if (loc.lat != null && loc.lng != null) return loc;
    }
  } catch { /* ignore */ }
  return null;
}

// localStorage can throw in Safari private mode or when over quota; never let
// that crash a click handler.
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

export function useLocation() {
  const [location, setLocation] = useState(() => {
    const saved = loadSavedLocation();
    if (saved) return { lat: saved.lat, lng: saved.lng, name: saved.name || '', source: 'saved' };
    return { lat: DEFAULT_LAT, lng: DEFAULT_LNG, name: DEFAULT_NAME, source: 'default' };
  });
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | requesting | granted | denied | unavailable

  // Try browser geolocation on mount (only if no saved location)
  useEffect(() => {
    const saved = loadSavedLocation();
    if (saved) return; // User has a saved preference, respect it
    if (window.Capacitor) return; // native app uses default location, no dialog

    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }

    let cancelled = false;
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        setLocation({
          lat: Math.round(pos.coords.latitude * 1000) / 1000,
          lng: Math.round(pos.coords.longitude * 1000) / 1000,
          name: 'Current Location',
          source: 'gps',
        });
        setGeoStatus('granted');
      },
      () => {
        if (cancelled) return;
        setGeoStatus('denied');
        // Keep default
      },
      { timeout: 8000, maximumAge: 300000 }
    );
    return () => { cancelled = true; };
  }, []);

  const setCustomLocation = useCallback((lat, lng, name) => {
    const loc = { lat, lng, name: name || `${lat.toFixed(3)}, ${lng.toFixed(3)}` };
    safeSet(STORAGE_KEY, JSON.stringify(loc));
    setLocation({ ...loc, source: 'custom' });
  }, []);

  const locateMe = useCallback(() => {
    if (!navigator.geolocation) return;
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Math.round(pos.coords.latitude * 1000) / 1000;
        const lng = Math.round(pos.coords.longitude * 1000) / 1000;
        const loc = { lat, lng, name: 'Current Location' };
        safeSet(STORAGE_KEY, JSON.stringify(loc));
        setLocation({ ...loc, source: 'gps' });
        setGeoStatus('granted');
      },
      () => { setGeoStatus('denied'); },
      { timeout: 8000 }
    );
  }, []);

  const resetToDefault = useCallback(() => {
    safeRemove(STORAGE_KEY);
    setLocation({ lat: DEFAULT_LAT, lng: DEFAULT_LNG, name: DEFAULT_NAME, source: 'default' });
  }, []);

  return { location, geoStatus, setCustomLocation, locateMe, resetToDefault };
}
